import React from 'react';
import PropTypes from 'prop-types';

/**
 * DataGrid - Responsive Grid Layout für DataCards
 * 
 * Usage:
 * <DataGrid
 *   columns={{ sm: 1, md: 2, lg: 3 }}
 *   gap="md"
 *   items={courses}
 *   renderItem={(course) => <DataCard ... />}
 *   emptyState={{
 *     icon: "📚",
 *     title: "Keine Kurse",
 *     description: "Erstelle deinen ersten Kurs",
 *     action: { label: "Kurs erstellen", onClick: () => {} }
 *   }}
 *   loading={isLoading}
 *   skeleton={{ count: 6, component: <DataCardSkeleton /> }}
 * />
 */

// Empty State Component
function EmptyState({ icon, title, description, action, actions }) {
  const allActions = actions || (action ? [action] : []);
  
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {icon && (
        <span className="text-6xl mb-4 opacity-50">{icon}</span>
      )}
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6">
          {description}
        </p>
      )}
      {allActions.length > 0 && (
        <div className="flex items-center gap-3">
          {allActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
                action.variant === 'secondary'
                  ? 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  : 'text-white bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

EmptyState.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['primary', 'secondary'])
  }),
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['primary', 'secondary'])
  }))
};

// Skeleton Card Component
function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="flex-1">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-16" />
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-20" />
      </div>
      <div className="flex justify-end">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24" />
      </div>
    </div>
  );
}

// Add Card (Create New)
function AddCard({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      className="min-h-[180px] flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 theme-transition text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
    >
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

AddCard.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired
};

// Main DataGrid Component
export default function DataGrid({
  items = [],
  renderItem,
  columns = { sm: 1, md: 2, lg: 3 },
  gap = 'md',
  emptyState,
  loading = false,
  skeleton = { count: 6 },
  addCard,
  className = ''
}) {
  // Gap classes
  const gapClasses = {
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-6'
  };

  // Generate responsive column classes
  const getColumnClasses = () => {
    const classes = [];
    
    if (columns.sm) classes.push(`grid-cols-${columns.sm}`);
    if (columns.md) classes.push(`md:grid-cols-${columns.md}`);
    if (columns.lg) classes.push(`lg:grid-cols-${columns.lg}`);
    if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`);
    
    return classes.join(' ');
  };

  // Use static classes for common grid layouts (Tailwind doesn't support dynamic classes)
  const columnClasses = {
    '1-2-3': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    '1-2-2': 'grid-cols-1 md:grid-cols-2',
    '1-2-4': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    '1-1-2': 'grid-cols-1 lg:grid-cols-2',
    '1-3-3': 'grid-cols-1 md:grid-cols-3',
    '2-3-4': 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
  };

  const getColumnKey = () => {
    return `${columns.sm || 1}-${columns.md || columns.sm || 1}-${columns.lg || columns.md || columns.sm || 1}`;
  };

  const gridColumnClass = columnClasses[getColumnKey()] || 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  // Loading state
  if (loading) {
    return (
      <div className={`grid ${gridColumnClass} ${gapClasses[gap]} ${className}`}>
        {Array.from({ length: skeleton.count }).map((_, index) => (
          <React.Fragment key={index}>
            {skeleton.component || <SkeletonCard />}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Empty state
  if (items.length === 0 && emptyState) {
    return <EmptyState {...emptyState} />;
  }

  // Render grid
  return (
    <div className={`grid ${gridColumnClass} ${gapClasses[gap]} ${className}`}>
      {items.map((item, index) => (
        <React.Fragment key={item.id || index}>
          {renderItem(item, index)}
        </React.Fragment>
      ))}
      
      {/* Add Card */}
      {addCard && (
        <AddCard onClick={addCard.onClick} label={addCard.label} />
      )}
    </div>
  );
}

DataGrid.propTypes = {
  items: PropTypes.array,
  renderItem: PropTypes.func.isRequired,
  columns: PropTypes.shape({
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number
  }),
  gap: PropTypes.oneOf(['sm', 'md', 'lg']),
  emptyState: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    action: PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    }),
    actions: PropTypes.array
  }),
  loading: PropTypes.bool,
  skeleton: PropTypes.shape({
    count: PropTypes.number,
    component: PropTypes.node
  }),
  addCard: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }),
  className: PropTypes.string
};

// Export EmptyState for standalone use
export { EmptyState };
